import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const BillingInfoForm = ({ onCreatePaymentIntent, amount, termsAccepted, bookingInfo, trailerData }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [billingInfo, setBillingInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    addressLine1: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setBillingInfo(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!billingInfo?.firstName || !billingInfo?.lastName || !billingInfo?.email ||
        !billingInfo?.addressLine1 || !billingInfo?.city || !billingInfo?.state || !billingInfo?.postalCode) {
      setError('Please fill in all required billing information');
      return;
    }

    if (!termsAccepted) {
      setError('Please accept all required terms and acknowledgments before continuing');
      return;
    }

    setIsProcessing(true);

    try {
      const { data, error: functionError } = await supabase?.functions?.invoke('create-payment-intent', {
        body: {
          bookingInfo,
          trailerData,
          billingInfo
        }
      });

      if (functionError) {
        // Extract the real error message from the edge function response body
        let errorMessage = 'Failed to initialize payment. Please try again.';
        try {
          if (functionError?.context) {
            const errorBody = await functionError?.context?.json();
            if (errorBody?.error) {
              errorMessage = errorBody?.error;
            }
          } else if (functionError?.message && !functionError?.message?.includes('non-2xx')) {
            errorMessage = functionError?.message;
          }
        } catch {
          // fallback to generic message
        }
        throw new Error(errorMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (!data?.clientSecret) {
        throw new Error('No client secret returned from payment service');
      }

      setIsProcessing(false);
      onCreatePaymentIntent(data?.clientSecret, data?.paymentIntentId, data?.customerId);
    } catch (err) {
      console.error('Payment intent creation error:', err);
      setError(err?.message || 'Failed to initialize payment. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="CreditCard" size={20} color="var(--color-primary)" strokeWidth={2} />
        </div>
        <h2 className="font-heading text-2xl font-semibold text-foreground">
          Billing Information
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-muted rounded-md mb-4">
          <p className="font-body text-sm text-foreground">
            Please provide your billing information to continue with payment.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="text"
            name="firstName"
            label="First Name"
            placeholder="John"
            value={billingInfo?.firstName}
            onChange={handleInputChange}
            required
          />
          <Input
            type="text"
            name="lastName"
            label="Last Name"
            placeholder="Doe"
            value={billingInfo?.lastName}
            onChange={handleInputChange}
            required
          />
        </div>

        <Input
          type="email"
          name="email"
          label="Email Address"
          placeholder="john.doe@example.com"
          value={billingInfo?.email}
          onChange={handleInputChange}
          required
        />

        <Input
          type="tel"
          name="phone"
          label="Phone Number"
          placeholder="555-123-4567"
          value={billingInfo?.phone}
          onChange={handleInputChange}
        />

        <Input
          type="text"
          name="addressLine1"
          label="Street Address"
          placeholder="123 Main Street"
          value={billingInfo?.addressLine1}
          onChange={handleInputChange}
          required
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            type="text"
            name="city"
            label="City"
            placeholder="Austin"
            value={billingInfo?.city}
            onChange={handleInputChange}
            required
          />
          <Input
            type="text"
            name="state"
            label="State"
            placeholder="TX"
            value={billingInfo?.state}
            onChange={handleInputChange}
            required
            description="2-letter code"
          />
          <Input
            type="text"
            name="postalCode"
            label="ZIP Code"
            placeholder="78701"
            value={billingInfo?.postalCode}
            onChange={handleInputChange}
            required
          />
        </div>

        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start space-x-2">
            <Icon name="AlertCircle" size={18} color="var(--color-destructive)" strokeWidth={2} />
            <p className="font-body text-sm text-destructive">{error}</p>
          </div>
        )}

        <Button
          type="submit"
          disabled={isProcessing}
          className="w-full"
        >
          {isProcessing ? (
            <>
              <Icon name="Loader" size={18} className="animate-spin mr-2" />
              Processing...
            </>
          ) : (
            `Continue to Payment — $${amount?.toFixed(2)}`
          )}
        </Button>
      </form>
    </div>
  );
};

export default BillingInfoForm;
